import { Amplify, Auth } from "aws-amplify";
import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from "react-router-dom";

import "./App.css";

import awsExports from "./aws-exports";
import Accounts from "./components/Accounts";
import AccountStatus from "./components/AccountStatus";
import RejectionDashboard from "./components/RejectionDashboard";
import TermsOfService from "./components/TermsOfService";
import RightPanel, {
  getCurrentAuthenticatedUserDeduped,
  isCognitoSessionExpiredError,
  hasValidCognitoSession,
  GlobalSignOutHandler
} from "./components/RightPanel";
import {
  getEmailAccountStatus,
  getToken,
  subscribeToAuthEvents,
  hasRefreshedAttrsForSub,
  markRefreshedAttrsForSub,
  getCookieDomain
} from "./utils";
import { getSubdomainInfo, getSubdomainRedirect, isOAuthCallbackInProgress, getActiveBaseDomain } from "./utils/subdomainDetector";
import { safeRedirect } from "./utils/safeRedirect";

// Configure Amplify with safe redirect behavior across environments.
// getCookieDomain is imported from ./utils (single source for App + RightPanel).

const normalizeRedirectList = (value) =>
  String(value || "")
    .split(",")
    .map((url) => url.trim())
    .filter(Boolean);

const currentOrigin = window.location.origin;
const isAmplifyAppDomain = window.location.hostname.endsWith("amplifyapp.com");
// Auth debug banner (?debugAuth) — dev-only AND localhost-only (OWASP A01/A09).
// Prevents accidental exposure of auth state on any deployed build.
const _isLocalDevHost = ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
const shouldShowAuthDebug = process.env.NODE_ENV !== "production"
  && _isLocalDevHost
  && new URLSearchParams(window.location.search).has("debugAuth");

function AppDebugBanner({ isCheckingAuth }) {
  if (!shouldShowAuthDebug) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 99999,
        background: "#fff4cc",
        color: "#2f2f2f",
        fontSize: "12px",
        padding: "6px 10px",
        borderBottom: "1px solid #e0c97a"
      }}
    >
      app.mounted=true | isCheckingAuth={String(isCheckingAuth)} | path={window.location.pathname}
    </div>
  );
}

const redirectSignInList = normalizeRedirectList(awsExports?.oauth?.redirectSignIn);
const redirectSignOutList = normalizeRedirectList(awsExports?.oauth?.redirectSignOut);

const matchesCurrentOrigin = (url) => {
  const normalizedUrl = String(url || '').trim();
  if (!normalizedUrl) {
    return false;
  }

  return (
    normalizedUrl === currentOrigin ||
    normalizedUrl === `${currentOrigin}/`
  );
};

const callbackUrlConfigured = redirectSignInList.some(matchesCurrentOrigin);
const signOutUrlConfigured = redirectSignOutList.some(matchesCurrentOrigin);
const shouldWarnAboutPreviewOAuthUrls =
  isAmplifyAppDomain &&
  (!callbackUrlConfigured || !signOutUrlConfigured) &&
  isOAuthCallbackInProgress();

if (shouldWarnAboutPreviewOAuthUrls) {
  console.warn(
    "Amplify app domain detected, but the current URL is missing from Cognito App Client callback/sign-out URLs:",
    `${currentOrigin}/`,
    {
      callbackUrlConfigured,
      signOutUrlConfigured
    }
  );
}

const amplifyConfig = {
  ...awsExports,
  oauth: {
    ...awsExports.oauth,
    redirectSignIn: redirectSignInList.join(","),
    redirectSignOut: redirectSignOutList.join(",")
  }
};

Amplify.configure(amplifyConfig);

// Apply cookie storage for all environments so that preview branches on
// amplifyapp.com behave identically to production (Finding #2 mitigation).
// On amplifyapp.com each branch has a unique hostname, so the domain is set to
// the exact hostname rather than a shared parent — cross-subdomain sharing is
// not possible on amplifyapp.com and this matches the production pattern.
if (Auth && typeof Auth.configure === "function") {
  const isHttps = window.location.protocol === "https:";
  Auth.configure({
    cookieStorage: {
      domain: getCookieDomain(),
      path: "/",
      expires: 365,
      sameSite: isHttps ? "none" : "lax",
      secure: isHttps
    }
  });
}

function AuthenticatedContent({ signOut, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Validating the session immediately handles the edge case where Authenticator
  // passes a user object without a session (e.g. immediately after TOTP success).
  const [activeUser, setActiveUser] = useState(user);
  const [isReady, setIsReady] = useState(false);
  const [statusError, setStatusError] = useState(null); // 'transient' | null
  const [subdomainInfo] = useState(() => getSubdomainInfo());
  const [isRecoveringSession, setIsRecoveringSession] = useState(false);
  const [recoveryTimedOut, setRecoveryTimedOut] = useState(false);
  const SESSION_RECOVERY_TIMEOUT_MS = 12000;
  // Prevent infinite loop: only refresh user attributes once per session
  const hasRefreshedAttributes = useRef(false);
  const signOutInFlightRef = useRef(false);
  
  const isFullyAuthenticated = hasValidCognitoSession(activeUser);

  // S3 (Addendum 3): listen for sign-out events broadcast by other tabs and
  // tear down this tab's UI in lockstep. Without this, Tab B would keep
  // rendering an "authenticated" view until its next API call returns 401.
  useEffect(() => {
    const unsubscribe = subscribeToAuthEvents((evt) => {
      if (evt?.type === "SIGNED_OUT") {
        setActiveUser(null);
        setIsReady(false);
        // Hard reload so React in-memory state is discarded everywhere.
        if (window.location.pathname !== "/") {
          window.location.replace("/");
        } else {
          window.location.reload();
        }
      }
    });
    return unsubscribe;
  }, []);

  // Sync prop 'user' to local state 'activeUser'
  useEffect(() => {
    let isMounted = true;

    // If the incoming user prop has a valid session, it is always the source of truth.
    if (hasValidCognitoSession(user)) {
      setActiveUser(user);
      setIsRecoveringSession(false);
      return () => {
        isMounted = false;
      };
    }

    // If the prop has a user but NO session, it might be the "stale" user after MFA.
    // Try to force-refresh session + user, so we avoid rendering a blank page.
    if (user && !hasValidCognitoSession(activeUser)) {
      setIsRecoveringSession(true);
      setRecoveryTimedOut(false);

      // Safety valve: if the session cannot be recovered within the timeout window,
      // stop the indefinite spinner and surface a "sign in again" prompt.
      const recoveryTimer = setTimeout(() => {
        if (isMounted) {
          setIsRecoveringSession(false);
          setRecoveryTimedOut(true);
        }
      }, SESSION_RECOVERY_TIMEOUT_MS);

      (async () => {
        try {
          try {
            // Populate token cache if possible.
            await Auth.currentSession();
          } catch {
            // Non-blocking: during MFA/setup this may legitimately fail.
          }

          // Retry a couple of times to reduce false negatives on transient network issues.
          // Keep retries small to avoid delaying the timeout-based UX.
          const retryDelaysMs = [0, 500, 1000];
          for (let i = 0; i < retryDelaysMs.length; i++) {
            if (retryDelaysMs[i] > 0) {
              await new Promise((resolve) => setTimeout(resolve, retryDelaysMs[i]));
            }

            let freshUser;
            try {
              freshUser = await getCurrentAuthenticatedUserDeduped();
            } catch {
              continue;
            }

            if (isMounted && hasValidCognitoSession(freshUser)) {
              clearTimeout(recoveryTimer);
              setActiveUser(freshUser);
              setIsRecoveringSession(false);
              setRecoveryTimedOut(false);
              break;
            }
          }
        } catch {
          // Still no valid session — timeout handler will surface the sign-in prompt.
        }
      })();

      return () => {
        clearTimeout(recoveryTimer);
        isMounted = false;
      };
    }

    return () => {
      isMounted = false;
    };
  }, [user, activeUser]);

  const safeSignOut = async () => {
    if (signOutInFlightRef.current) {
      return;
    }

    signOutInFlightRef.current = true;

    try {
      // Best-effort: warm getToken so any in-flight refresh resolves before sign-out,
      // but don't block sign-out on it.
      if (hasValidCognitoSession(activeUser)) {
        await Promise.race([
          getToken({ expectedEmail: activeUser?.attributes?.email }).catch(() => null),
          new Promise((resolve) => setTimeout(resolve, 5000))
        ]);
      }
    } catch (error) {
      console.warn("Non-blocking pre-signout token warm-up failed", error);
    } finally {
      // 1. Wipe all top-level component caching to prevent UI flashes
      setActiveUser(null);
      setIsReady(false);
      
      // Close the sliding panel before the global handler reloads the page
      if (typeof window.closeWorkflowPanel === 'function') {
        window.closeWorkflowPanel();
      }

      // S2: ensure a true global sign-out (revokes refresh tokens at Cognito)
      // regardless of which Authenticator prop was supplied. We wrap the
      // supplied `signOut` so GlobalSignOutHandler calls Cognito's GlobalSignOut
      // first, then performs the local cleanup it already does today.
      const wrappedSignOut = async () => {
        try {
          await Auth.signOut({ global: true });
        } catch (err) {
          console.warn("Global sign-out call failed, falling back to local sign-out", err);
          try { await signOut(); } catch { /* ignore */ }
        }
      };

      // Use the global handler for consistent cleanup
      try {
        await GlobalSignOutHandler(wrappedSignOut);
      } catch (signOutError) {
        console.warn("Global sign out handler failed, attempting direct signout", signOutError);
        try {
           await signOut();
        } catch(e) { /* ignore */ }
      }
      signOutInFlightRef.current = false;
    }
  };

  useLayoutEffect(() => {
    if (!activeUser || !isFullyAuthenticated) {
      return;
    }

    setIsReady(false);
    setStatusError(null);
  }, [location.pathname, activeUser, isFullyAuthenticated]);

  useEffect(() => {
    let isMounted = true;
    const checkAccountStatus = async () => {
      if (activeUser && isFullyAuthenticated) {
        try {
          // effectiveUser tracks the authoritative user object for this execution context.
          // It starts as activeUser but is replaced with refreshedUser if attributes are
          // refreshed mid-execution. Using activeUser directly would reference a stale
          // closure value and cause getEmailAccountStatus to call /account?email=undefined.
          let effectiveUser = activeUser;

          // Check if user needs to be redirected to their company subdomain
          let redirectUrl = getSubdomainRedirect(activeUser, subdomainInfo);

          // If on main domain and no redirect found, double-check by forcing a user attribute refresh.
          // Sometimes the initial user object from Authenticator lacks custom attributes despite having a session.
          if (!redirectUrl && !subdomainInfo.isSubdomain) {
             const hasCompanyAttr = activeUser.attributes?.['custom:company_name'] || 
                                    activeUser.signInUserSession?.idToken?.payload?.['custom:company_name'];

             // Only force refresh if the attribute is missing AND we haven't refreshed yet for this Cognito sub.
             // S10: persist the "refreshed" flag in sessionStorage keyed by sub so it survives
             // route remounts within a tab while not leaking across users/sessions.
             const sub = activeUser?.attributes?.sub || activeUser?.signInUserSession?.idToken?.payload?.sub;
             const alreadyRefreshed = hasRefreshedAttrsForSub(sub) || hasRefreshedAttributes.current;
             if (hasCompanyAttr === undefined && !alreadyRefreshed) {
               try {
                 console.log('Refreshing user attributes to check for company subdomain...');
                 hasRefreshedAttributes.current = true; // Set before async call to prevent concurrent refreshes
                 markRefreshedAttrsForSub(sub);
                 const refreshedUser = await getCurrentAuthenticatedUserDeduped();
                 
                 // Update both the local reference and React state.
                 // setActiveUser alone does NOT update the current execution context —
                 // effectiveUser must be updated here so subsequent API calls use the
                 // correct email instead of the old (possibly incomplete) user object.
                 effectiveUser = refreshedUser;
                 if (isMounted) setActiveUser(refreshedUser);
                 
                 redirectUrl = getSubdomainRedirect(refreshedUser, subdomainInfo);
               } catch (e) {
                 console.warn('Failed to refresh user attributes:', e);
                 // Proceed with existing user
               }
             }
          }

          if (redirectUrl) {
            console.log('Redirecting to company subdomain:', redirectUrl);
            // OWASP A01: validate against host allowlist before navigating.
            safeRedirect(redirectUrl, '/');
            return;
          }

          // Fetch account status with up to 4 attempts and exponential backoff.
          // This prevents DynamoDB throttle spikes from incorrectly sending a
          // rejected user to the main dashboard.
          let accounts = null;
          const backoffDelays = [1500, 3000, 5000];
          let allTransient = false;
          console.log('App.js: checking account status');
          for (let attempt = 0; attempt <= backoffDelays.length; attempt++) {
            if (attempt > 0) {
              await new Promise((resolve) => setTimeout(resolve, backoffDelays[attempt - 1]));
              if (!isMounted) return;
            }
            try {
              accounts = await getEmailAccountStatus(effectiveUser);
              allTransient = false;
              if (accounts && Array.isArray(accounts)) break;
            } catch (err) {
              if (err?.name !== 'TransientBackendError') throw err;
              allTransient = true;
            }
          }

          if (!accounts || !Array.isArray(accounts)) {
            if (allTransient) {
              // All attempts returned 503 — don't fall through to the dashboard
              // (which would flash "account not found"). Let the user refresh.
              console.warn('App.js: All status-check attempts returned TransientBackendError. Showing retry screen.');
              setStatusError('transient');
              return;
            }
            console.log('App.js: No accounts found or user may be deleted/disabled. Proceeding to dashboard.');
            setIsReady(true);
            return;
          }

          console.log('App.js: Account status check returned records');

          const normalizeStatus = (record) =>
            String(record?.request_status || record?.status || "").toUpperCase();

          const parseTime = (value) => {
            if (!value) return 0;
            const time = Date.parse(value);
            return Number.isNaN(time) ? 0 : time;
          };

          const getRecordTime = (record) =>
            parseTime(record?.updated_at) ||
            parseTime(record?.rejected_at) ||
            parseTime(record?.approved_at) ||
            parseTime(record?.closed_at) ||
            parseTime(record?.created_at);

          const getStatusRank = (record) => {
            const status = normalizeStatus(record);
            const statusRanks = {
              PENDING: 1,
              PENDING_APPROVAL: 2,
              PROVISIONING: 3,
              CONFIGURING: 4,
              REJECTED: 100,
              ACCOUNT_CREATION_REJECTED: 101,
              FAILED: 110,
              TIMED_OUT: 111,
              DASHBOARD_CREATION_FAILED: 112,
              FAILED_DASHBOARD_DEPLOYMENT: 112,
              INFRASTRUCTURE_CREATION_FAILED: 113,
              FAILED_INFRASTRUCTURE_DEPLOYMENT: 113,
              FAILED_IMAGE_SHARING: 114,
              CLOSURE_FAILED: 115,
              IMAGE_PERMISSION_DELETION_FAILED: 116,
              CLOSURE_VALIDATION_FAILED: 117,
              STACKSET_DELETION_FAILED: 118,
              PENDING_SETTLEMENT: 119,
              ACCOUNT_CLOSED: 120
            };

            return statusRanks[status] || 0;
          };

          const getLatestRecord = (records) =>
            [...records].sort((a, b) => {
              const timeDiff = getRecordTime(b) - getRecordTime(a);
              if (Math.abs(timeDiff) >= 5000) {
                return timeDiff;
              }

              return getStatusRank(b) - getStatusRank(a);
            })[0] || null;
          
          // Priority 1: In-progress closure (CLOSURE_PENDING, DELETING_*)
          const inProgressClosureStatuses = [
            'CLOSURE_PENDING',
            'DELETING_DASHBOARD',
            'DELETING_INFRASTRUCTURE'
          ];
          
          const closureInProgress = accounts.find((record) => {
            const status = normalizeStatus(record);
            return inProgressClosureStatuses.includes(status);
          });

          if (closureInProgress) {
            console.log('App.js: Closure in progress detected; redirecting to status page');
            const email = closureInProgress.account_email;
            const company = closureInProgress.company_name;
            const targetPath = `/status/${encodeURIComponent(email)}/${encodeURIComponent(company)}`;
            if (window.location.pathname !== targetPath) {
              navigate(targetPath, { 
                replace: true, 
                state: { 
                  accountData: closureInProgress,
                  activityType: 'closure' 
                } 
              });
            }
            setIsReady(true);
            return;
          }

          if (
            (location.pathname.startsWith('/closure-status/') || location.pathname.startsWith('/status/')) &&
            location.state?.pendingClosureNavigation
          ) {
            console.log('App.js: Preserving pending status route while backend status propagates');
            setIsReady(true);
            return;
          }

          // Priority 2: Active account (APPROVED or COMPLETED)
          // Skip if any record is ACCOUNT_CLOSED or PENDING_SETTLEMENT — closure supersedes active status.
          const hasClosedFinalStatus = accounts.some((record) => {
            const s = normalizeStatus(record);
            return s === 'ACCOUNT_CLOSED' || s === 'PENDING_SETTLEMENT';
          });
          const approvedAccount = !hasClosedFinalStatus && accounts.find((record) => {
            const status = normalizeStatus(record);
            return status === "APPROVED" || status === "COMPLETED";
          });

          if (approvedAccount) {
            console.log('App.js: Active account detected; routing to main dashboard if not viewing status');
            if (window.location.pathname !== '/' && !window.location.pathname.startsWith('/status/')) {
              navigate('/', { replace: true });
            }
            setIsReady(true);
            return;
          }

          // Priority 3+: for non-active/non-closure states, route based on the
          // latest relevant request record rather than the first matching status.
          // This prevents a stale older PENDING_APPROVAL row from overriding a
          // newer REJECTED row at login.
          const inProgressCreationStatuses = [
            'PENDING',
            'PENDING_APPROVAL',
            'CREATING_ACCOUNT',
            'ACCOUNT_CREATED',
            'DEPLOYING_INFRASTRUCTURE',
            'INFRASTRUCTURE_DEPLOYED',
            'CREATING_DASHBOARD',
            'DASHBOARD_DEPLOYED',
            'PROVISIONING',
            'CONFIGURING'
          ];

          const latestRelevantRecord = getLatestRecord(
            accounts.filter((record) => {
              const status = normalizeStatus(record);
              return (
                inProgressCreationStatuses.includes(status) ||
                status === 'REJECTED' ||
                status === 'ACCOUNT_CREATION_REJECTED' ||
                status === 'FAILED' ||
                status === 'TIMED_OUT' ||
                status === 'FAILED_DASHBOARD_DEPLOYMENT' ||
                status === 'FAILED_INFRASTRUCTURE_DEPLOYMENT' ||
                status === 'DASHBOARD_CREATION_FAILED' ||
                status === 'INFRASTRUCTURE_CREATION_FAILED' ||
                status === 'FAILED_IMAGE_SHARING' ||
                status === 'CLOSURE_FAILED' ||
                status === 'IMAGE_PERMISSION_DELETION_FAILED' ||
                status === 'CLOSURE_VALIDATION_FAILED' ||
                status === 'STACKSET_DELETION_FAILED' ||
                status === 'PENDING_SETTLEMENT' ||
                status === 'ACCOUNT_CLOSED'
              );
            })
          );

          const latestRelevantStatus = normalizeStatus(latestRelevantRecord);

          if (
            latestRelevantStatus === 'REJECTED' ||
            latestRelevantStatus === 'ACCOUNT_CREATION_REJECTED'
          ) {
            console.log('App.js: Latest relevant record is REJECTED; redirecting to rejection dashboard');
            if (window.location.pathname !== '/rejection') {
              navigate('/rejection', { replace: true, state: { rejectedAccount: latestRelevantRecord } });
            }
            setIsReady(true);
            return;
          }

          if (
            latestRelevantStatus === 'FAILED' ||
            latestRelevantStatus === 'TIMED_OUT' ||
            latestRelevantStatus === 'FAILED_DASHBOARD_DEPLOYMENT' ||
            latestRelevantStatus === 'FAILED_INFRASTRUCTURE_DEPLOYMENT' ||
            latestRelevantStatus === 'DASHBOARD_CREATION_FAILED' ||
            latestRelevantStatus === 'INFRASTRUCTURE_CREATION_FAILED' ||
            latestRelevantStatus === 'FAILED_IMAGE_SHARING' ||
            latestRelevantStatus === 'CLOSURE_FAILED' ||
            latestRelevantStatus === 'IMAGE_PERMISSION_DELETION_FAILED' ||
            latestRelevantStatus === 'CLOSURE_VALIDATION_FAILED' ||
            latestRelevantStatus === 'STACKSET_DELETION_FAILED' ||
            latestRelevantStatus === 'PENDING_SETTLEMENT' ||
            latestRelevantStatus === 'ACCOUNT_CLOSED' ||
            inProgressCreationStatuses.includes(latestRelevantStatus)
          ) {
            console.log('App.js: Latest relevant record is terminal failure or in-progress; redirecting to status');
            const email = latestRelevantRecord.account_email;
            const company = latestRelevantRecord.company_name;
            const targetPath = `/status/${encodeURIComponent(email)}/${encodeURIComponent(company)}`;
            if (window.location.pathname !== targetPath) {
              navigate(targetPath, { replace: true, state: { accountData: latestRelevantRecord } });
            }
            setIsReady(true);
            return;
          }

          // Default: No special routing needed, proceed to main dashboard
          setIsReady(true);
        } catch (error) {
          console.error("Failed to check account status", error);
          if (isCognitoSessionExpiredError(error)) {
            await safeSignOut();
            return;
          }
          setIsReady(true); // Proceed even if status check fails
        }
      }
    };

    checkAccountStatus();
    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeUser,
    isFullyAuthenticated,
    navigate,
    location.pathname,
    subdomainInfo.isSubdomain,
    subdomainInfo.subdomain
  ]);
  
  // During MFA / setup challenges Amplify can briefly provide a `user` object while
  // the session isn't fully established yet. Never render a blank page here.
  if (!activeUser || !isFullyAuthenticated) {
    if (recoveryTimedOut) {
      return (
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100vh", gap: "1rem" }}>
          <p style={{ margin: 0 }}>Your session could not be restored after TOTP setup.</p>
          <button
            onClick={async () => {
              await Promise.resolve(signOut?.());
            }}
          >
            Sign in again
          </button>
        </div>
      );
    }
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <span className="loader"></span>
        <span style={{ marginLeft: "0.75rem" }}>
          {isRecoveringSession ? "Completing authentication…" : "Loading…"}
        </span>
      </div>
    );
  }

  if (!isReady) {
    if (statusError === 'transient') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: '1rem' }}>
          <p style={{ margin: 0 }}>We're having trouble connecting. Please refresh the page.</p>
          <button onClick={() => window.location.reload()}>Refresh</button>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <span className="loader"></span>
        <span style={{ marginLeft: "0.75rem" }}>Loading your dashboard…</span>
      </div>
    );
  }
  
  return (
    <>
      <nav className="navbar">
        <div className="nav-content">
          <div className="nav-user-info">
            <span className="nav-username">{activeUser.username || 'User'}</span>
            {subdomainInfo.isSubdomain && (
              <span className="nav-subdomain">
                @ {subdomainInfo.companyName}
              </span>
            )}
          </div>
          <button onClick={safeSignOut}>Sign out</button>
        </div>
      </nav>
      <main>
        <Routes>
          <Route 
            path="/" 
            element={
              <Accounts
                loggedInUser={activeUser}
                isAdmin={activeUser?.attributes?.["custom:roles"] === "SUPER_ADMIN"}
                onSignOut={safeSignOut}
              />
            } 
          />
          <Route
            path="/rejection"
            element={<RejectionDashboard loggedInUser={activeUser} onSignOut={safeSignOut} />}
          />
          <Route
            path="/status/:accountEmail/:companyName"
            element={<AccountStatus loggedInUser={activeUser} onSignOut={safeSignOut} />}
          />
          <Route
            path="/terms-of-service"
            element={<TermsOfService />}
          />
        </Routes>
      </main>
    </>
  );
}

export default function App() {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [subdomainInfo] = useState(() => getSubdomainInfo());

  useEffect(() => {
    let isMounted = true;

    const checkSubdomainAuth = async () => {
      if (!subdomainInfo.isSubdomain) {
        if (isMounted) setIsCheckingAuth(false);
        return;
      }

      // Do not interrupt OAuth callback processing.
      if (isOAuthCallbackInProgress()) {
        if (isMounted) setIsCheckingAuth(false);
        return;
      }

      try {
        await getCurrentAuthenticatedUserDeduped();
        if (isMounted) setIsCheckingAuth(false);
      } catch (error) {
        // C3: derive the main-domain redirect target from getActiveBaseDomain()
        // so adding a new brand domain only requires one helper update.
        // OWASP A01: route through safeRedirect so off-allowlist hosts cannot
        // be reached even if getActiveBaseDomain() is ever tampered with.
        const mainDomainUrl = `${window.location.protocol}//${getActiveBaseDomain()}/`;
        safeRedirect(mainDomainUrl, '/');
      }
    };

    checkSubdomainAuth();

    return () => {
      isMounted = false;
    };
  }, [subdomainInfo.isSubdomain]);

  if (isCheckingAuth) {
    return (
      <>
        <AppDebugBanner isCheckingAuth={isCheckingAuth} />
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
          <span className="loader"></span>
          <span style={{ marginLeft: "0.75rem" }}>Loading…</span>
        </div>
      </>
    );
  }

  return (
    <>
      <AppDebugBanner isCheckingAuth={isCheckingAuth} />
      <Router>
        <RightPanel>
          {({ signOut, user }) => (
            <AuthenticatedContent signOut={signOut} user={user} />
          )}
        </RightPanel>
      </Router>
    </>
  );
}
